import config from "config/config";
import { authService } from "./authService";
import { createUrlSearchParams } from "helpers";
import {
    fetchBundleDashboardConfigListV2Request,
    fetchDashboardConfigListV2Request,
} from "./types/DashboardServices";

export const dashboardService = {
    fetchDashboardGMW,
    fetchSalesChannel,
    fetchDashboardAnalytics,
    fetchBusinessStats,
    fetchTopPerforming,
    fetchLeastPerforming,
    fetchTopOnHandProduct,
    fetchProductsProfitMargin,
    fetchTopOnHandProductList,
    fetchProductsProfitList,
    fetchSalesGMW,
    fetchDashboardConfigList,
    addForecastConfig,
    fetchBundleDashboardGMW,
    fetchBundleDashboardConfigList,
    fetchBundleDashboardGMWTooltip,
    fetchNonFinishedGoodsForecast,
    fetchFinishedGoodsForecast,
    fetchBundledGoodsForecast,
    pinForecastConfig,
    unpinForecastConfig,
    fetchDashboardConfigListV2,
    fetchBundleDashboardConfigListV2,
};

async function fetchDashboardGMW(
    category?: string,
    product?: string,
    channel?: string,
    startDate?: string,
    endDate?: string,
    dateRange?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        category: category as string,
        channel: channel as string,
        startDate: startDate as string,
        endDate: endDate as string,
        // dateRange: dateRange as string,
    });

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/dashboard/growth-rate?${
            dateRange ? `dateRange=${dateRange}` : ""
        }${product ? `&inventoryId=${product}` : ""}${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }`,
        requestOptions
    );
    return res;
}

async function fetchBundleDashboardGMW(
    category?: string,
    product?: string,
    channel?: string,
    startDate?: string,
    endDate?: string,
    dateRange?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        category: category as string,
        channel: channel as string,
        startDate: startDate as string,
        endDate: endDate as string,
        // dateRange: dateRange as string,
    });

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/dashboard/growth-rate/master-sku?${
            dateRange ? `dateRange=${dateRange}` : ""
        }${product ? `&inventoryId=${product}` : ""}${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }`,
        requestOptions
    );
    return res;
}

async function fetchSalesGMW(
    category?: string,
    inventoryId?: string,
    warehouse?: string,
    channel?: string,
    startDate?: string,
    endDate?: string,
    dateRange?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        category: category as string,
        // inventoryId: product as string,
        warehouseId: warehouse as string,
        channel: channel as string,
        startDate: startDate as string,
        endDate: endDate as string,
        // dateRange: dateRange as string,
    });

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/single/growth-rate?${
            inventoryId ? `inventoryId=${inventoryId}` : ""
        }${dateRange ? `&dateRange=${dateRange}` : ""}${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }`,
        requestOptions
    );
    return res;
}

async function fetchSalesChannel() {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/sales-channel`,
        requestOptions
    );
    return res;
}

async function fetchDashboardAnalytics(period?: string, startDate?: string, endDate?: string) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        startDate: startDate as string,
        endDate: endDate as string,
    });

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/analytics/dashboard/sales?${
            period ? `dateRange=${period}` : ""
        }${Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""}`,
        requestOptions
    );
    return res;
}

async function fetchBusinessStats() {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/analytics/business-stats`,
        requestOptions
    );
    return res;
}

async function fetchTopPerforming(
    limit?: number,
    page?: number,
    inventoryId?: string,
    warehouseId?: string,
    search?: string,
    channel?: string,
    dateRange?: string,
    sortParams?: { [key: string]: number }
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        page,
        inventoryId: inventoryId as string,
        warehouseId: warehouseId as string,
        search: search as string,
        channel: channel as string,
        dateRange: dateRange as string,
    });

    const key = typeof sortParams === "object" && sortParams !== null && Object.keys(sortParams)[0];
    const value = key ? sortParams[key] : null;
    const sortParamsFormat = key && value !== undefined ? `${key}=${value}` : "";

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/list/top-performing-products?limit=${limit}${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }${key ? "&" + sortParamsFormat?.toString() : ""}`,
        requestOptions
    );
    return res;
}

async function fetchLeastPerforming(
    limit?: number,
    page?: number,
    inventoryId?: string,
    warehouseId?: string,
    search?: string,
    channel?: string,
    dateRange?: string,
    sortParams?: { [key: string]: number }
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        page,
        inventoryId: inventoryId as string,
        warehouseId: warehouseId as string,
        search: search as string,
        channel: channel as string,
        dateRange: dateRange as string,
    });

    const key = typeof sortParams === "object" && sortParams !== null && Object.keys(sortParams)[0];
    const value = key ? sortParams[key] : null;
    const sortParamsFormat = key && value !== undefined ? `${key}=${value}` : "";

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/list/worse-performing-products?limit=${limit}${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }${key ? "&" + sortParamsFormat?.toString() : ""}`,
        requestOptions
    );
    return res;
}

async function fetchTopOnHandProduct() {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/get/top-onhand-products`,
        requestOptions
    );
    return res;
}

async function fetchProductsProfitMargin() {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/all/products-profit-margin-contribution`,
        requestOptions
    );
    return res;
}

async function fetchProductsProfitList(
    limit?: number,
    inventoryId?: string,
    channel?: string,
    dateRange?: string,
    search?: string,
    sortParams?: { [key: string]: number }
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        inventoryId: inventoryId as string,
        search: search as string,
        channel: channel as string,
        dateRange: dateRange as string,
    });

    const key = typeof sortParams === "object" && sortParams !== null && Object.keys(sortParams)[0];
    const value = key ? sortParams[key] : null;
    const sortParamsFormat = key && value !== undefined ? `${key}=${value}` : "";

    const res = await authService.apiGate(
        `${
            config.API_URL
        }/api/v1/ldb/inventory/list/products-profit-margin-contribution-table?limit=${limit}${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }${key ? "&" + sortParamsFormat?.toString() : ""}`,
        requestOptions
    );
    return res;
}

async function fetchTopOnHandProductList(
    limit?: number,
    inventoryId?: string,
    channel?: string,
    dateRange?: string,
    search?: string,
    sortParams?: { [key: string]: number }
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        inventoryId: inventoryId as string,
        search: search as string,
        channel: channel as string,
        dateRange: dateRange as string,
    });

    const key = typeof sortParams === "object" && sortParams !== null && Object.keys(sortParams)[0];
    const value = key ? sortParams[key] : null;
    const sortParamsFormat = key && value !== undefined ? `${key}=${value}` : "";

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/list/inventory-onhand-details?limit=${limit}${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }${key ? "&" + sortParamsFormat?.toString() : ""}`,
        requestOptions
    );
    return res;
}

async function fetchDashboardConfigList(
    page?: number,
    dateRange?: string,
    productId?: string,
    channel?: string,
    category?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        page: page as number,
        dateRange: dateRange as string,
        channel,
        category,
    });

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/dashboard/forecast-configs?${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }${productId ? `&inventoryId=${productId}` : ""}`,
        requestOptions
    );

    return res;
}

async function fetchDashboardConfigListV2(request: fetchDashboardConfigListV2Request) {
    const { params } = request;
    const requestOptions = {
        method: "GET",
    };

    const searchParams = new URLSearchParams({
        ...(params.page && { page: params.page.toString() }),
        ...(params.dateRange && { dateRange: params.dateRange }),
        ...(params.channel && { channel: params.channel }),
        ...(params.category && { category: params.category }),
        ...(params.productId && { inventoryId: params.productId }),
    });

    const baseUrl = new URL(`${config.API_URL}/api/v1/ldb/inventory/dashboard/forecast-configs`);
    baseUrl.search = searchParams.toString();

    const res = await authService.apiGate(baseUrl.toString(), requestOptions);

    return res;
}

async function addForecastConfig(data: Record<string, any> | Array<Record<string, any>>) {
    try {
        const requestOptions = {
            method: "POST",
            body: JSON.stringify(data),
        };

        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/inventory/add/forecast-config`,
            requestOptions
        );

        return res;
    } catch (error) {
        throw error;
    }
}

async function fetchBundleDashboardConfigList(
    page?: number,
    dateRange?: string,
    productId?: string,
    channel?: string,
    category?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        page: page as number,
        dateRange: dateRange as string,
        channel,
        category,
    });

    const searchParams = new URLSearchParams({
        ...(page && { page: page.toString() }),
        ...(dateRange && { dateRange }),
        ...(channel && { channel }),
        ...(category && { category }),
        ...(productId && { inventoryId: productId }),
    });

    const baseUrl = new URL(
        `${config.API_URL}/api/v1/ldb/inventory/dashboard/forecast-configs/master-sku`
    );
    baseUrl.search = searchParams.toString();

    const res = await authService.apiGate(baseUrl.toString(), requestOptions);

    return res;
}

async function fetchBundleDashboardConfigListV2(request: fetchBundleDashboardConfigListV2Request) {
    const { params } = request;
    const requestOptions = {
        method: "GET",
    };

    const searchParams = new URLSearchParams({
        ...(params.page && { page: params.page.toString() }),
        ...(params.dateRange && { dateRange: params.dateRange }),
        ...(params.channel && { channel: params.channel }),
        ...(params.category && { category: params.category }),
    });

    const baseUrl = new URL(
        `${config.API_URL}/api/v1/ldb/inventory/dashboard/forecast-configs/master-sku`
    );
    baseUrl.search = searchParams.toString();

    const res = await authService.apiGate(baseUrl.toString(), requestOptions);

    return res;
}

async function fetchBundleDashboardGMWTooltip(
    category?: string,
    product?: string,
    channel?: string,
    startDate?: string,
    endDate?: string,
    dateRange?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        category: category as string,
        channel: channel as string,
        startDate: startDate as string,
        endDate: endDate as string,
        // dateRange: dateRange as string,
    });

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/dashboard/growth-rate/isBundle/tool-tips?${
            dateRange ? `dateRange=${dateRange}` : ""
        }${product ? `&inventoryId=${product}` : ""}${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }`,
        requestOptions
    );
    return res;
}

async function fetchNonFinishedGoodsForecast(
    page?: number,
    dateRange?: string,
    productId?: string,
    channel?: string,
    category?: string,
    search?: string,
    startDate?: string,
    endDate?: string,
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        page: page as number,
        dateRange: dateRange as string,
        channel,
        category,
    });

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/dashboard/forecast-configs/non-finished?${
            Boolean(urlParams?.toString()) ? "&" + urlParams?.toString() : ""
        }${productId ? `&inventoryId=${productId}` : ""}${search ? `&search=${search}` : ""}${
            startDate && endDate ? `&startDate=${startDate}&endDate=${endDate}` : ""
        }`,
        requestOptions
    );

    return res;
}

async function fetchFinishedGoodsForecast(
    page?: number,
    dateRange?: string,
    productId?: string,
    channel?: string,
    category?: string,
    search?: string,
    startDate?: string,
    endDate?: string,
    warehouseId?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const searchParams = new URLSearchParams({
        ...(page && { page: page.toString() }),
        ...(dateRange && { dateRange }),
        ...(channel && { channel }),
        ...(category && { category }),
        ...(productId && { inventoryId: productId }),
        ...(search && { search }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(warehouseId && { warehouseId }),
    });

    const baseUrl = new URL(`${config.API_URL}/api/v1/ldb/inventory/get-inventory-forecast`);
    baseUrl.search = searchParams.toString();

    const res = await authService.apiGate(baseUrl.toString(), requestOptions);
    return res;
}

async function fetchBundledGoodsForecast(
    page?: number,
    dateRange?: string,
    productId?: string,
    channel?: string,
    category?: string,
    search?: string,
    startDate?: string,
    endDate?: string,
    warehouseId?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const searchParams = new URLSearchParams({
        ...(page && { page: page.toString() }),
        ...(dateRange && { dateRange }),
        ...(channel && { channel }),
        ...(category && { category }),
        ...(productId && { inventoryId: productId }),
        ...(search && { search }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(warehouseId && { warehouseId }),
    });

    const baseUrl = new URL(`${config.API_URL}/api/v1/ldb/inventory/get-bundle-forecast`);
    baseUrl.search = searchParams.toString();

    const res = await authService.apiGate(baseUrl.toString(), requestOptions);

    return res;
}

async function pinForecastConfig(inventoryId: string) {
    const requestOptions = {
        method: "PUT",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/pin/${inventoryId}`,
        requestOptions
    );

    return res;
}

async function unpinForecastConfig(inventoryId: string) {
    const requestOptions = {
        method: "PUT",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/inventory/unpin/${inventoryId}`,
        requestOptions
    );

    return res;
}
